<template>
  <div class="analytics-line-chart">
    <h3 class="analytics-line-chart__title">{{ title }}</h3>
    <div class="analytics-line-chart__checkbox-group-container">
      <span>{{ subTitle }}</span>
      <el-checkbox-group class="analytics-line-chart__checkbox-group" v-model="selectedOption">
        <Checkbox
          v-for="option in filteredOptions"
          :key="option.value"
          :value="option.value"
          :label="option.label"
          :disabled="option.disabled"
        >
          {{ option.label }}
        </Checkbox>
      </el-checkbox-group>
    </div>
    <LineChartElement
      class="analytics-line-chart__line-chart"
      ref="lineChart"
      :key="chartKey"
      :data="filteredLineDataChart"
      :show-legend="false"
      :tiks-collor="tiksColor"
      :tiks-size="tiksSize"
      :show-grid-lines="showGridLines"
    />
    <div
      v-if="filteredLineDataChart.datasets.length"
      class="analytics-line-chart__platforms-container"
    >
      <div class="analytics-line-chart__platforms-title">{{ platformsTitle }}</div>
      <div class="analytics-line-chart__platforms">
        <div
          v-for="platform in filteredPlatforms"
          :key="platform.name"
          class="analytics-line-chart__platforms-item"
          :class="platform.name"
        >
          <LineBorderSvg
            :color="platform.borderColor"
            :dash="platform.borderDash"
            :width="20"
            :height="4"
          />
          {{ platform.name }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Checkbox from '@/components/elements/Checkbox/Checkbox';
import LineBorderSvg from '@/components/elements/LineBorderSvg';
import { AnalyticsType } from '@/utils/common';

export default {
  name: 'AnalyticsLineChart',
  components: {
    Checkbox,
    LineBorderSvg,
    LineChartElement: () => import('@/components/elements/ReportsLineChartElement'),
  },
  props: {
    type: {
      type: String,
      validator: function (value) {
        return Object.values(AnalyticsType).includes(value);
      },
    },
    title: {
      type: String,
      default: 'График',
    },
    subTitle: {
      type: String,
      default: 'Отображение площадок на графике:',
    },
    options: {
      type: Array,
      default: () => [],
    },
    lineDataChart: {
      type: Object,
      default: () => ({
        labels: [],
        datasets: [],
      }),
    },
    platforms: {
      type: Array,
      default: () => [],
    },
    tiksColor: {
      type: String,
      default: '#BFC2C6',
    },
    tiksSize: {
      type: Number,
      default: 14,
    },
    showGridLines: {
      type: Boolean,
      default: false,
    },
    platformsTitle: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      selectedOption: [],
      isVisible: false,
      observer: null,
    };
  },
  computed: {
    filteredOptions() {
      if (this.type !== AnalyticsType.OVERVIEW) {
        return this.options;
      }

      return this.options.map((option) => ({
        ...option,
        disabled: !this.lineDataChart.datasets.some(
          (dataset) => dataset.label === option.label && dataset.data.some((value) => value !== 0)
        ),
      }));
    },
    chartKey() {
      return this.$store.state.Analytics.activeSubTab;
    },
    filteredLineDataChart() {
      return {
        labels: this.lineDataChart.labels,
        datasets:
          this.type === AnalyticsType.OVERVIEW
            ? this.lineDataChart.datasets.filter(
                (dataset) =>
                  this.selectedOption.includes(dataset.label) &&
                  dataset.data.some((value) => value !== 0)
              )
            : this.lineDataChart.datasets,
      };
    },
    filteredPlatforms() {
      if (this.type === AnalyticsType.OVERVIEW) {
        return this.platforms.filter((platform) =>
          this.filteredLineDataChart.datasets.some((dataset) => dataset.label === platform.name)
        );
      } else {
        return this.platforms;
      }
    },
  },
  watch: {
    options: {
      handler(newVal) {
        // Выбираем только те опции, которые имеют данные
        this.selectedOption = [
          ...newVal
            .filter((option) => {
              if (this.type !== AnalyticsType.OVERVIEW) return true;

              return this.lineDataChart.datasets.some(
                (dataset) =>
                  dataset.label === option.label && dataset.data.some((value) => value !== 0)
              );
            })
            .map((option) => option.label),
        ];
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/helpers/variables';
@import '@/assets/styles/helpers/mixin';

.analytics-line-chart {
  padding: 30px;
  border-radius: 6px;
  background: $basicWhite;

  @include below(768px) {
    padding: 0;
    background: transparent;
  }

  &__title {
    font-weight: 700;
    font-size: 24px;
    color: $basicBlack;
    margin-bottom: 20px;
  }

  &__checkbox-group-container {
    font-weight: 400;
    font-size: 16px;
    line-height: 100%;
    color: $gray;
    margin-bottom: 20px;
  }

  &__checkbox-group {
    display: inline;
    margin-left: 20px;
  }

  &__platforms-title {
    color: $basicBlack;
    font-weight: 700;
    font-size: 14px;
    margin-bottom: 10px;
  }

  &__platforms {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 20px;
  }

  &__platforms-item {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 400;
    font-size: 14px;
    color: $basicBlack;
  }

  &__platforms-container {
    margin-top: 20px;
  }
}
</style>
